import { v4 as uuidv4 } from "uuid";
import { updateIdx } from "../objects.js";
import { getDefaultAttrsSettingsForDesign, getDefaultFactsSettingsForDesign, getDefaultEventsSettingsForDesign, getDefaultThemeSettings } from "@/constants/ui.js";
import typo from "ru-typo";

export class ObjectLinkList {
  constructor(list, cleanUp = false, store = null) {
    this.is_deleted = list.is_deleted ?? false
    this.id = list.id ?? ""
    this.uuid = list.uuid ?? list.id
    this.label = list.label ?? ""
    this.items = (list.items ?? []).map(item => new ObjectLink(item))
    this.is_event_list = list.is_event_list ?? false
    this.is_article_list = list.is_article_list ?? false
    this.ui_data = list.ui_data ?? {}
    if (!this.is_event_list) {
      this.design = list.design ?? "square"
      if (this.design && store) {
        const designSettings = getDefaultThemeSettings(this.design, 'links_objects', store)
        const themes = designSettings?.themes
        const themeName = this.ui_data?.design_theme
        const themeSettings = themes?.find(t => t.theme === themeName) || themes?.[0] || getDefaultEventsSettingsForDesign(this.design)
        this.design_extra = list.design_extra ? new DesignExtra({ ...themeSettings, ...list.design_extra }) : new DesignExtra(themeSettings)
        if (!this.ui_data.design_theme) {
          this.ui_data = { ...this.ui_data, design_theme: themes?.[0]?.theme }
        }
      } else {
        this.design_extra = list.design_extra ? new DesignExtra({ ...getDefaultEventsSettingsForDesign(this.design), ...list.design_extra }) : new DesignExtra(getDefaultEventsSettingsForDesign(this.design))
      }
    } else {
      this.design = list.design ?? "links_events_default"
      if (this.design && store) {
        const designSettings = getDefaultThemeSettings(this.design, 'links_events', store)
        const themes = designSettings?.themes
        const themeName = this.ui_data?.design_theme
        const themeSettings = themes?.find(t => t.theme === themeName) || themes?.[0] || getDefaultEventsSettingsForDesign(this.design)
        this.design_extra = list.design_extra ? new DesignExtra({ ...themeSettings, ...list.design_extra }) : new DesignExtra(themeSettings)
        if (!this.ui_data.design_theme) {
          this.ui_data = { ...this.ui_data, design_theme: themes?.[0]?.theme }
        }
      } else {
        this.design_extra = list.design_extra ? new DesignExtra({ ...getDefaultEventsSettingsForDesign(this.design), ...list.design_extra }) : new DesignExtra(getDefaultEventsSettingsForDesign(this.design))
      }
    }

    if (cleanUp) {
      delete this.uuid
      !this.id && delete this.id
      if (!this.is_event_list) {
        this.design = this.design ?? "square"
      } else {
        this.design = this.design ?? "links_events_default"
      }
      this.items = updateIdx(this.items.map(item => new ObjectLink(item, true)))
    }
  }
}

export class ObjectLink {
  constructor(link, cleanUp = false) {
    this.is_deleted = link.is_deleted ?? false
    this.id = link.id ?? ""
    this.uuid = link.uuid ?? link.id
    this.idx = link.idx ?? 0
    this.text = link.text ?? ""
    this.target_object = link.target_object ?? undefined
    this.target_object_annotated = link.target_object_annotated ?? undefined
    this.use_full_label = link.use_full_label ?? false

    if (cleanUp) {
      delete this.uuid
      delete this.target_object_annotated
      !this.id && delete this.id
      if (this.target_object && typeof this.target_object === 'object') {
        this.target_object = this.target_object.id ?? this.target_object.value
      }
    }
  }
}

export class DesignExtra {
  constructor(data = {}) {
    this.bg = {
      color: data?.bg?.color ?? null,
      palette: data?.bg?.palette ?? null,
      gradient_enabled: data?.bg?.gradient_enabled ?? false,
      gradient_direction: data?.bg?.gradient_direction ?? 'bottom',
      gradient_shadow_direction: data?.bg?.gradient_shadow_direction ?? 'transparent',
    }
    this.block_border = {
      color: data?.block_border?.color ?? null,
      palette: data?.block_border?.palette ?? null,
    }
    this.icon_border = {
      color: data?.icon_border?.color ?? null,
      palette: data?.icon_border?.palette ?? null,
    }
    this.icon_bg = {
      color: data?.icon_bg?.color ?? null,
      palette: data?.icon_bg?.palette ?? null,
      gradient_enabled: data?.icon_bg?.gradient_enabled ?? false,
      gradient_direction: data?.icon_bg?.gradient_direction ?? 'bottom',
      gradient_shadow_direction: data?.icon_bg?.gradient_shadow_direction ?? 'transparent',
    }
    this.icon_color = {
      color: data?.icon_color?.color ?? null,
    }
    this.text = {
      color: data?.text?.color ?? null,
    }
    this.mask = {
      name: data?.mask?.name ?? null,
    }
    this.first_block = {
      title_color: data?.first_block?.title_color ?? null,
      block_border_color: data?.first_block?.block_border_color ?? null,
      icon_border_color: data?.first_block?.icon_border_color ?? null,
      bg_color: data?.first_block?.bg_color ?? null,
      gradient_enabled: data?.first_block?.gradient_enabled ?? false,
      gradient_direction: data?.first_block?.gradient_direction ?? 'bottom',
      gradient_shadow_direction: data?.first_block?.gradient_shadow_direction ?? 'transparent',
    }
  }
}

export class ChartDesignExtra {
  constructor(data = {}) {
    this.axis_text = {
      color: data?.axis_text?.color ?? '#2f4f4f',
    }
    this.axis_rotate = {
      value: data?.axis_rotate?.value ?? 90,
    }
    this.axis_z = {
      enabled: data?.axis_z?.enabled ?? 'none',
    }
    this.series_color = {
      color: data?.series_color?.color ?? null,
      palette: data?.series_color?.palette ?? null,
    }
    this.circle_inner_empty = {
      value: data?.circle_inner_empty?.value ?? 45,
    }
    this.chart_bg = {
      color: data?.chart_bg?.color ?? '#f5f5f5',
    }
  }
}

export class AttrsList {
  constructor(list, cleanUp = false, to_representation = false) {
    const defaultDesign = list.items?.length ? (list.items[0]?.image_type === 'image' ? 'links_images_minimalism' : 'links_icons_minimalism') : undefined

    this.is_deleted = list.is_deleted ?? 0
    this.id = list.id ?? ""
    this.uuid = list.uuid ?? list.id
    this.label = list.label ?? ""
    this.vistat_object = list.vistat_object ?? undefined
    this.sublabel = (list.sublabel ?? "").substring(0, 50)
    this.design = list.design ?? defaultDesign
    if (this.design) {
      this.design_extra = list.design_extra ? new DesignExtra({ ...getDefaultAttrsSettingsForDesign(this.design), ...list.design_extra }) : new DesignExtra(getDefaultAttrsSettingsForDesign(this.design))
    } else {
      this.design_extra = list.design_extra ? new DesignExtra(list.design_extra) : new DesignExtra()
    }
    this.icon = list.icon ?? null
    // this.is_summary = list.is_summary ?? false
    this.items = (list.items ?? []).map(item => new AttrsListItem(item, cleanUp, to_representation))

    if (cleanUp) {
      delete this.uuid
      !this.id && delete this.id
      this.design = this.design ?? defaultDesign ?? "links_icons_minimalism"
    }
  }
}

export class AttrsListItem {
  getShortText() {
    if (this.text) {
      if (this.text.includes('[SEP]')) {
        return this.text.split('[SEP]')[0]
      }
      // return truncateString(this.text)
    }
    return this.text
  }

  constructor(item, cleanUp = false, to_representation = false) {
    this.is_deleted = item.is_deleted ?? 0
    this.id = item.id ?? ""
    this.uuid = item.uuid ?? item.id
    this.idx = item.idx ?? 0
    this.text = item.text ?? ""
    this.label = item.label ?? ""
    this.icon = item.icon ?? ""
    this.image = item.image
    this.image_file = item.image_file
    this.image_type = item.image_type ?? "icon"
    this.vistat_object = item.vistat_object ?? undefined
    this.no_image = item.no_image ?? false
    this.image_url = item.image_url ?? null

    if (to_representation) {
      this.shortText = typo(this.getShortText(), { hyphens: true })
      this.fullText = typo(this.text.replaceAll('[SEP]', ''), { hyphens: true })
      this.label = typo(this.label, { hyphens: true })
    }

    if (cleanUp) {
      this.image instanceof Object && delete this.image
      delete this.uuid
      delete this.shortText
      delete this.fullText
      !this.id && delete this.id
      if (this.image_type === "image") {
        delete this.icon
      }
      if (this.image_type === "icon") {
        delete this.image
        delete this.image_url
      }
    }
  }
}


export class Tgb {
  constructor(data, cleanUp = false, store) {
    this.is_deleted = data.is_deleted ?? 0
    this.id = data.id ?? null
    this.idx = data.idx ?? 0
    this.label = data.label ?? ""
    this.text = data.text ?? ""
    this.icon = data.icon ?? null
    this.background = {
      type: data?.background?.type ?? 'none',
      background_id: data?.background?.background_id ?? null,
      image_depot_id: data?.background?.image_depot_id ?? null,
      image_uploaded: data?.background?.image_uploaded ?? null,
    }
    this.linked_object = data.linked_object ?? undefined
    this.vistat_object = data.vistat_object ?? undefined
    this.design = data.design ?? undefined
    this.ui_data = data.ui_data ?? {}
    if (data.design && store) {
      this.design_extra = data.design_extra ? new DesignExtra({ ...getDefaultThemeSettings(data.design, 'tgbs', store)?.themes[0], ...data.design_extra }) : new DesignExtra(getDefaultThemeSettings(data.design, 'tgbs', store)?.themes[0])
    } else {
      this.design_extra = data.design_extra ? new DesignExtra(data.design_extra) : new DesignExtra()
    }

    if (cleanUp) {
      delete this.idx
      !this.id && delete this.id
      this.design = this.design ?? "tgbs_default"
    }
  }
}

export class Table {
  constructor(list, cleanUp = false, store = null) {
    this.is_deleted = list.is_deleted ?? 0
    this.id = list.id ?? ""
    this.idx = list.idx ?? 0
    this.label = list.label ?? ""
    this.design = list.design ?? undefined
    this.ui_data = list.ui_data ? { show: 'all', ...list.ui_data } : { show: 'all' }
    if (list.design && store) {
      this.design_extra = list.design_extra ? new DesignExtra({ ...getDefaultThemeSettings(list.design, 'tables', store)?.themes[0], ...list.design_extra }) : new DesignExtra(getDefaultThemeSettings(list.design, 'tables', store)?.themes[0])
    } else {
      this.design_extra = list.design_extra ? new DesignExtra(list.design_extra) : new DesignExtra()
    }
    this.vistat_object = list.vistat_object ?? undefined
    const tableData = list.data ?? {}
    const chartsSource = Array.isArray(tableData.charts) ? [...tableData.charts] : []
    if (tableData.chart_type) {
      chartsSource.push({
        chart_type: tableData.chart_type,
        chart_theme: tableData.chart_theme,
        chart_label: tableData.chart_label,
        chart_direction: tableData.chart_direction,
        chart_with_value: tableData.chart_with_value,
        chart_axis: tableData.chart_axis,
        chart_meta: tableData.chart_meta,
        chart_styles: tableData.chart_styles,
      })
    }
    const normalizeChart = (chart = {}, idx = 0) => {
      const axis = chart.chart_axis || chart.axis || {}
      const key = typeof axis.key !== "undefined" ? axis.key : null
      const group = typeof axis.group !== "undefined" ? axis.group : null
      const values = Array.isArray(axis.values) ? axis.values : []
      return {
        is_deleted: chart.is_deleted ?? 0,
        id: chart.id ?? "",
        uuid: chart.uuid ?? chart.id ?? uuidv4(),
        idx: chart.idx ?? idx,
        chart_label: chart.chart_label ?? chart.label ?? "",
        chart_type: chart.chart_type ?? chart.type ?? undefined,
        chart_theme: chart.chart_theme ?? chart.theme ?? undefined,
        chart_direction: chart.chart_direction ?? chart.direction ?? null,
        chart_with_value: chart.chart_with_value ?? chart.with_value ?? false,
        chart_axis: { key, values, group },
        chart_meta: chart.chart_meta ?? chart.meta ?? {},
        chart_styles: chart.chart_styles ?? chart.styles ?? new ChartDesignExtra()
      }
    }
    const normalizedCharts = chartsSource.map((chart, idx) => normalizeChart(chart, idx))
    this.data = {
      rows: tableData.rows ?? [['', ''], ['','']],
      columns: tableData.columns ?? [],
      is_numeric: tableData.is_numeric ?? false,
      amount: tableData.amount ?? { columns: [], rows: [] },
      options: tableData.options ?? {
        minDimensions: [2, 2],
        allowInsertRow: true,
        allowInsertColumn: true,
        allowDeleteRow: true,
        allowDeleteColumn: true
      },
      meta: tableData.meta ?? {},
      charts: normalizedCharts
    }

    if (cleanUp) {
      delete this.idx
      !this.id && delete this.id
      this.design = this.design ?? "tables_default"
    }
  }
}

export class PropertiesLink {
  constructor(link, cleanUp = false) {
    this.is_deleted = link.is_deleted ?? false
    this.id = link.id ?? ""
    this.uuid = link.uuid ?? link.id
    this.label = link.label ?? ""
    this.vistat_object = link.vistat_object ?? ""

    if (cleanUp) {
      delete this.uuid
      !this.id && delete this.id

      if (typeof this.vistat_object === "object") this.vistat_object = this.vistat_object.id
    }
  }
}

export class Facts {
  constructor(facts = Object(), cleanUp = false, store = null) {
    this.label = facts.label ?? "Интересные факты"
    this.design = facts.design ?? "facts_default"
    this.ui_data = facts.ui_data ?? {}
    if (this.design && store) {
      const designSettings = getDefaultThemeSettings(this.design, 'facts', store)
      const themes = designSettings?.themes
      const themeName = this.ui_data?.design_theme
      const themeSettings = themes?.find(t => t.theme === themeName) || themes?.[0] || getDefaultFactsSettingsForDesign(this.design)
      this.design_extra = facts.design_extra ? new DesignExtra({ ...themeSettings, ...facts.design_extra }) : new DesignExtra(themeSettings)
      if (!this.ui_data.design_theme) {
        this.ui_data = { ...this.ui_data, design_theme: themes?.[0]?.theme ?? this.design }
      }
    } else {
      this.design_extra = facts.design_extra ? new DesignExtra({ ...getDefaultFactsSettingsForDesign(this.design), ...facts.design_extra }) : new DesignExtra(getDefaultFactsSettingsForDesign(this.design))
    }
    this.items = (facts.items ?? []).map(item => new Fact(item, cleanUp))
    if (cleanUp) {
      this.items = updateIdx(this.items)
    }
  }
}


export class Fact {
  constructor(fact = Object(), cleanUp = false) {
    this.is_deleted = fact.is_deleted ?? false
    this.id = fact.id ?? ""
    this.idx = fact.idx ?? 0
    this.uuid = fact.uuid ?? fact.id
    this.icon = fact.icon ?? undefined
    this.description = fact.description ?? ""

    this.value = fact.value || {
      type: "", data: undefined, si: undefined
    }

    if (cleanUp) {
      delete this.uuid
      !this.id && delete this.id
    }
  }
}


export class FreeInfographic {
  constructor(data = Object()) {
    this.id = data.id ?? null,
    this.uuid = data.uuid ?? data.id,
    this.idx = data.idx ?? 0,
    this.label = data.label ?? "",
    // this.polygons = data.polygons ?? { coordinates: { width: data?.polygons?.coordinates?.width ?? 847, height: data?.polygons?.coordinates?.width ?? 847, left:0, top:0 }, aspectRatio: 1, polygons: data?.polygons?.polygons ?? [] }
    this.polygons = data.polygons ?? { coordinates: { width: data?.polygons?.coordinates?.width ?? 847, height: data?.polygons?.coordinates?.height ?? 847, left:0, top:0 }, aspectRatio: data?.polygons?.aspectRatio, polygons: data?.polygons?.polygons ?? [], bg_styles: data?.polygons?.bg_styles ?? {} },
    this.image_file = data.image_file && data.image_file instanceof Blob ? data.image_file : null,
    this.image_url = data.image_url ?? null,
    this.base_url = data.base_url ?? null,
    this.no_image = data.no_image ?? false
  }
}

export class Quotes {
  constructor(quotes = Object(), cleanUp = false) {
    this.items = (quotes.items ?? []).map(item => new Quote(item, cleanUp))
    this.label = quotes.label || ""
    if (cleanUp) {
      this.items = updateIdx(this.items)
    }
  }
}


export class Quote {
  constructor(quote = Object(), cleanUp = false) {
    this.is_deleted = quote.is_deleted ?? false
    this.id = quote.id ?? ""
    this.idx = quote.idx ?? 0
    this.uuid = quote.uuid ?? quote.id
    this.icon = quote.icon ?? undefined
    this.text = quote.text ?? ""
    this.context = quote.context ?? ""
    this.subjects = quote.subjects ?? []
    this.date = quote.date ?? {
      vfrom: "",
      vfrom_approx: false,
      vto: "",
      vto_approx: false,
    }
    this.dateHumanReadable = quote.dateHumanReadable ?? null

    if (cleanUp) {
      delete this.uuid
      delete this.dateHumanReadable
      !this.id && delete this.id

      if (!this.subjects.length) delete this.subjects
      if (!this.date?.vfrom) delete this.date
      if (!this.context.length) delete this.context
    }
  }
}

export class PublicObject {
  constructor(vistatObject) {
    const data = { ...vistatObject }
    if (!Number.isNaN(data.date_score)) {
      const PERCENTAGE_SCORE = Number(data.date_score * 100)
      data.date_score = Number(PERCENTAGE_SCORE.toFixed(2));
      data.date_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }

    if (!Number.isNaN(data.total_score)) {
      const PERCENTAGE_SCORE = Number(data.total_score * 100)
      data.total_score = Number(PERCENTAGE_SCORE.toFixed(2));
      data.total_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }

    if (!Number.isNaN(data.scoring_score)) {
      const PERCENTAGE_SCORE = Number(data.scoring_score * 100)
      data.scoring_score = Number(PERCENTAGE_SCORE.toFixed(2));
      data.scoring_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }

    if (data.map && Object.values(data.map).length) data.map = cleanUpDataOfMap(data.map)
    if (data.map_dynamic?.map_config && Object.values(data.map_dynamic.map_config).length) data.map_dynamic.map_config = cleanUpDataOfMap(data.map_dynamic.map_config)

    Object.assign(this, data)
  }

  toDict() {
    const res = {}
    Object.keys(this).forEach(key => res[key] = this[key])
    return res
  }
}

export const cleanUpDataOfMap = (map = null) => {
  if (map === null) return null

  if (Object.keys(map.geodata).length) {
    const sizePolyMap = {
      'polygon': 4,
      'polyline': 2,
      'circle': 1
    }
    Object.keys(map.geodata).forEach(key => {
      if (!map.geodata[key].hasOwnProperty('geometry')) {
        return delete map.geodata[key]
      }

      map.geodata[key].geometry = map.geodata[key].geometry.filter(geometry => {
        return !Array.isArray(geometry) || geometry.length >= sizePolyMap[map.geodata[key].type]
      })

      if (!map.geodata[key].geometry.length) {
        return delete map.geodata[key]
      }
    })
  }

  return map
}

export class TextDraft {
  constructor(data = {}) {
    this.id = data.id ?? null,
    this.data_blocks = (data.data_blocks?.length ? data.data_blocks : []).map(item => ({ ...item }))
    this.ai_blocks = (data.ai_blocks?.length ? data.ai_blocks : []).map(item => ({
      ...item,
      sources: Array.isArray(item.sources) ? [...item.sources] : [],
    }))
  }
}
